import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ArrowUpIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface PreviewPerson {
  name: string;
  photo: string;
}
interface PreviewBadge {
  label: string;
  labelEs?: string;
  number: string;
  bg: string;
}
interface SectionAccordionProps {
  title: string;
  titleEs: string;
  children: React.ReactNode;
  bgColor: string;
  borderColor: string;
  textColor: string;
  iconColor: string;
  previewImages?: string[];
  previewPeople?: PreviewPerson[];
  previewBadges?: PreviewBadge[];
}
export function SectionAccordion({
  title,
  titleEs,
  children,
  bgColor,
  borderColor,
  textColor,
  iconColor,
  previewImages,
  previewPeople,
  previewBadges
}: SectionAccordionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    sectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const hasPreview = previewImages || previewPeople || previewBadges;
  const showImagePreview = !isOpen && previewImages && previewImages.length > 0;
  const showPeoplePreview = !isOpen && previewPeople && previewPeople.length > 0;
  const showBadgePreview = !isOpen && previewBadges && previewBadges.length > 0;
  const marqueeImages = previewImages ? [...previewImages, ...previewImages] : [];
  const marqueePeople = previewPeople ? [...previewPeople, ...previewPeople] : [];
  const marqueeBadges = previewBadges ? [...previewBadges, ...previewBadges, ...previewBadges, ...previewBadges] : [];
  return <motion.div ref={sectionRef} layout className={`rounded-xl overflow-hidden border transition-shadow duration-300 ${borderColor} ${isOpen ? 'shadow-lg' : 'shadow-sm hover:shadow-md sm:min-h-[10rem]'}`} data-id="element-2329">
      <button onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} className={`w-full flex items-center gap-3 sm:gap-4 group transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue focus-visible:ring-inset ${bgColor} ${isOpen ? 'px-6 sm:px-8 py-4 sm:py-5' : `px-4 sm:px-6 py-3 sm:py-3.5 ${!hasPreview ? 'sm:min-h-[10rem]' : ''}`}`} data-id="element-2330">
        {/* Accent dot */}
        <motion.div animate={{
        scale: isOpen ? 1.2 : 1,
        opacity: isOpen ? 1 : 0.7
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className={`flex-shrink-0 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${iconColor.replace('text-', 'bg-')} group-hover:opacity-100 transition-opacity`} data-id="element-2331" />

        {/* Title */}
        <h2 className={`font-bebas tracking-wider leading-none flex-shrink-0 text-left transition-all duration-200 ${textColor} ${isOpen ? 'text-2xl sm:text-3xl' : 'text-lg sm:text-xl'}`} data-id="element-2332">
          {isEs ? titleEs : title}
        </h2>

        {/* Spacer */}
        <div className="flex-1" data-id="element-2333" />

        {/* Hint text (only when collapsed, no previews) */}
        {!isOpen && !hasPreview && <span className={`hidden sm:block font-nunito text-[11px] tracking-wide uppercase flex-shrink-0 ${textColor} opacity-0 group-hover:opacity-50 transition-opacity duration-200`} data-id="element-2334">
            {isEs ? 'Explorar' : 'Explore'}
          </span>}

        {/* Chevron with circle bg */}
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className={`flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-200 ${isOpen ? 'w-8 h-8 sm:w-9 sm:h-9 bg-white/15' : 'w-7 h-7 sm:w-8 sm:h-8 bg-black/5 group-hover:bg-black/10'}`} data-id="element-2335">
          <ChevronDownIcon className={`transition-all duration-200 ${iconColor} ${isOpen ? 'w-5 h-5' : 'w-4 h-4'}`} data-id="element-2336" />
        </motion.div>
      </button>

      {/* Scrolling image preview */}
      {showImagePreview && <div className={`${bgColor} px-4 sm:px-6 pb-4 hidden sm:block`} onClick={() => setIsOpen(!isOpen)} style={{
      cursor: 'pointer'
    }} data-id="element-2337">
          <div className="relative h-24 overflow-hidden rounded-lg" style={{
        maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)'
      }} data-id="element-2338">
            <div className="flex gap-3 animate-marquee-news absolute top-0 left-0 h-full" data-id="element-2339">
              {marqueeImages.map((src, i) => <img key={i} src={src} alt="" className="h-24 w-40 object-cover rounded-lg flex-shrink-0 opacity-80 hover:opacity-100 transition-opacity duration-300" data-id="element-2340" />)}
            </div>
          </div>
        </div>}

      {/* Scrolling people preview */}
      {showPeoplePreview && <div className={`${bgColor} px-4 sm:px-6 pb-4 hidden sm:block`} onClick={() => setIsOpen(!isOpen)} style={{
      cursor: 'pointer'
    }} data-id="element-2341">
          <div className="relative h-28 overflow-hidden rounded-lg" style={{
        maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)'
      }} data-id="element-2342">
            <div className="flex gap-6 animate-marquee-news absolute top-0 left-0 h-full items-center" data-id="element-2343">
              {marqueePeople.map((person, i) => <div key={i} className="flex flex-col items-center gap-1.5 flex-shrink-0" data-id="element-2344">
                  <img src={person.photo} alt={person.name} className="w-16 h-16 rounded-full object-cover object-top border-2 border-white/50 shadow-sm opacity-85 hover:opacity-100 transition-opacity duration-300" data-id="element-2345" />
                  <span className={`font-nunito text-[11px] font-semibold tracking-wide whitespace-nowrap ${textColor} opacity-70`} data-id="element-2346">
                    {person.name}
                  </span>
                </div>)}
            </div>
          </div>
        </div>}

      {/* Scrolling badge preview */}
      {showBadgePreview && <div className={`${bgColor} px-4 sm:px-6 pb-4 hidden sm:block`} onClick={() => setIsOpen(!isOpen)} style={{
      cursor: 'pointer'
    }} data-id="element-2347">
          <div className="relative h-24 overflow-hidden rounded-lg" style={{
        maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)'
      }} data-id="element-2348">
            <div className="flex gap-4 animate-marquee-news absolute top-0 left-0 h-full items-center" data-id="element-2349">
              {marqueeBadges.map((badge, i) => <div key={i} className={`flex-shrink-0 ${badge.bg} rounded-xl h-20 w-44 flex flex-col items-center justify-center gap-1 shadow-md`} data-id="element-2350">
                  <span className="font-bebas text-white/30 text-2xl leading-none" data-id="element-2351">
                    {badge.number}
                  </span>
                  <span className="font-bebas text-white text-lg tracking-wider leading-none" data-id="element-2352">
                    {isEs && badge.labelEs ? badge.labelEs : badge.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>}

      {/* Expanded content */}
      <AnimatePresence initial={false} data-id="element-2353">
        {isOpen && <motion.div key="accordion-content" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-2354">
            <div data-id="element-2355">{children}</div>
            <div className={`flex justify-center py-4 ${bgColor}`} data-id="element-2356">
              <button onClick={scrollToTop} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-nunito text-sm font-bold tracking-wide transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue ${textColor} bg-white/20 hover:bg-white/30 backdrop-blur-sm`} data-id="element-2357">
                <ArrowUpIcon className="w-4 h-4" data-id="element-2358" />
                {isEs ? 'Volver Arriba' : 'Back to Top'}
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}